import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { fetchWithAuth } from '../../lib/api/fetchWithAuth';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-hot-toast';

interface SendOfferModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOfferSent: () => void;
  brandId: string;
  orgId: string;
}

export function SendOfferModal({ isOpen, onClose, onOfferSent, brandId, orgId }: SendOfferModalProps) {
  const { getAccessTokenSilently } = useAuth0();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    email: '',
    retainerAmount: '',
    commission: '',
    postsPerMonth: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const token = await getAccessTokenSilently();
      await fetchWithAuth(`/api/organizations/${orgId}/brands/${brandId}/applicants/direct-offer`, {
        method: 'POST',
        token,
        body: JSON.stringify({
          ...formData,
          retainerAmount: parseFloat(formData.retainerAmount),
          commission: parseFloat(formData.commission),
          postsPerMonth: parseInt(formData.postsPerMonth, 10)
        })
      });

      toast.success('Offer sent successfully');
      onOfferSent();
      onClose();
      setFormData({
        firstName: '',
        email: '',
        retainerAmount: '',
        commission: '',
        postsPerMonth: ''
      });
    } catch (error) {
      console.error('Error sending offer:', error);
      toast.error('Failed to send offer');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-md rounded-lg bg-gray-800 p-6 w-full">
          <Dialog.Title className="text-lg font-medium text-gray-100 mb-4">
            Send Direct Offer
          </Dialog.Title>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-300">
                First Name
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                className="mt-1 block w-full rounded-md border-gray-700 bg-gray-700 text-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="mt-1 block w-full rounded-md border-gray-700 bg-gray-700 text-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label htmlFor="retainerAmount" className="block text-sm font-medium text-gray-300">
                Monthly Retainer ($)
              </label>
              <input
                type="number"
                id="retainerAmount"
                name="retainerAmount"
                value={formData.retainerAmount}
                onChange={handleChange}
                required
                min="0"
                step="0.01"
                className="mt-1 block w-full rounded-md border-gray-700 bg-gray-700 text-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label htmlFor="commission" className="block text-sm font-medium text-gray-300">
                Commission (%)
              </label>
              <input
                type="number"
                id="commission"
                name="commission"
                value={formData.commission}
                onChange={handleChange}
                required
                min="0"
                max="100"
                step="0.1"
                className="mt-1 block w-full rounded-md border-gray-700 bg-gray-700 text-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label htmlFor="postsPerMonth" className="block text-sm font-medium text-gray-300">
                Posts Per Month
              </label>
              <input
                type="number"
                id="postsPerMonth"
                name="postsPerMonth"
                value={formData.postsPerMonth}
                onChange={handleChange}
                required
                min="1"
                className="mt-1 block w-full rounded-md border-gray-700 bg-gray-700 text-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-gray-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Sending...' : 'Send Offer'}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
} 